import {Stack, Typography} from '@mui/material'
import {useEffect, useState} from 'react'
import {Link} from 'react-router-dom'
import {PATHS, STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import Button from 'src/components-new/primitives/Button'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import AuthLayout from 'src/components-new/layout/AuthLayout'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'

const EMAIL_REGEX = /^\w+([+.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/

interface LoginConfiguration {
  logoPSP?: string
  logoIA?: string
  logoSponsorPsp?: string
  sponsorText?: string
  primaryColor: string
  PSPTitleAndIcon?: {title: string; icon: string}
}

interface PasswordRecoveryPageNewProps {
  config: LoginConfiguration
}

const PasswordRecoveryPageNew = ({config}: PasswordRecoveryPageNewProps) => {
  const [email, setEmail] = useState('')
  const [submitStatus, setSubmitStatus] = useState<string>()
  const [touched, setTouched] = useState({email: false})

  const isFormValid = EMAIL_REGEX.test(email)
  const isSubmitting = submitStatus === STATUS.PENDING

  useEffect(() => {
    if (config.PSPTitleAndIcon) {
      if (config.PSPTitleAndIcon.title) document.title = config.PSPTitleAndIcon.title
      const link = document.querySelector('link[rel="shortcut icon"]')
      if (link && config.PSPTitleAndIcon.icon) {
        link.setAttribute('href', `data:image/png;base64,${config.PSPTitleAndIcon.icon}`)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  useEffect(() => {
    if (submitStatus === STATUS.FULFILLED) {
      const timer = setTimeout(() => {
        window.location.href = `${window.location.protocol}//${window.location.host}${PATHS.LOGIN}`
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [submitStatus])

  const clearError = () => {
    if (submitStatus === STATUS.REJECTED) setSubmitStatus(undefined)
  }

  const handleEmailChange = (v: string) => {
    setEmail(v)
    clearError()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({email: true})
    if (!isFormValid) return
    await apiClient.changePassword({email}, setSubmitStatus)
  }

  const errors = {
    email: touched.email && !EMAIL_REGEX.test(email) ? 'Inserisci un\'email valida' : '',
  }

  const logo = config.logoPSP ? (
    <img
      alt='logoPSP'
      src={`data:image/png;base64,${config.logoPSP}`}
      style={{maxHeight: 64, maxWidth: 200, objectFit: 'contain'}}
    />
  ) : undefined

  const footer = (config.logoIA || config.logoSponsorPsp) ? (
    <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={2}>
      {config.logoIA && (
        <img
          alt='logoIA'
          src={`data:image/png;base64,${config.logoIA}`}
          style={{maxHeight: 22, objectFit: 'contain'}}
        />
      )}
      {config.logoSponsorPsp && (
        <Stack direction='row' alignItems='center' spacing={1}>
          {config.sponsorText && (
            <Typography variant='caption' sx={{color: NEUTRAL_SCALE[500]}}>
              {config.sponsorText}
            </Typography>
          )}
          <img
            alt='logoSponsor'
            src={`data:image/png;base64,${config.logoSponsorPsp}`}
            style={{maxHeight: 18, maxWidth: 100, objectFit: 'contain'}}
          />
        </Stack>
      )}
    </Stack>
  ) : undefined

  return (
    <AuthLayout logo={logo} footer={footer}>
      <Stack component='form' onSubmit={handleSubmit} spacing={2.5}>
        {submitStatus === STATUS.REJECTED && (
          <InlineMessage severity='error'>
            {'Alcuni dati inseriti non sono corretti. Verifica l\'email.'}
          </InlineMessage>
        )}

        {submitStatus === STATUS.FULFILLED && (
          <InlineMessage severity='success' title='Richiesta inviata'>
            {'A breve riceverai un\'email con il link per modificare la tua password. Verrai reindirizzato al login.'}
          </InlineMessage>
        )}

        <TextFieldNew
          label='Email'
          name='email'
          type='email'
          value={email}
          onChange={handleEmailChange}
          onBlur={() => setTouched(t => ({...t, email: true}))}
          placeholder='es. mario.rossi@email.com'
          autoComplete='email'
          autoFocus
          fullWidth
          required
          disabled={isSubmitting || submitStatus === STATUS.FULFILLED}
          error={!!errors.email}
          helperText={errors.email}
        />

        <Button
          type='submit'
          variant='primary'
          fullWidth
          disabled={!isFormValid || submitStatus === STATUS.FULFILLED}
          loading={isSubmitting}
          sx={{mt: 0.5}}
        >
          {'Invia'}
        </Button>

        <Stack direction='row' justifyContent='flex-start'>
          <Link to={PATHS.LOGIN} style={{textDecoration: 'none'}}>
            <Typography variant='body2' sx={{color: 'primary.main'}}>
              {'Torna al login'}
            </Typography>
          </Link>
        </Stack>
      </Stack>
    </AuthLayout>
  )
}

export default PasswordRecoveryPageNew
